/**
 * "Import into bulk import →" — the auto path out of a finished scraper job.
 *
 * The manual path is what this replaces, and it was four steps of carrying a
 * file by hand: download the result JSON, navigate back to the brand, find the
 * import screen, upload the file it had just been given. Every one of those
 * steps was moving bytes the admin already had.
 *
 * So this fetches the result through the callable that already exists
 * (`getExtractorJobResult` — deployed with Step 3, no new function), resolves
 * the brand the job was for, and mounts the bulk-import wizard on it with the
 * envelope pre-loaded. The wizard's own pipeline is untouched from there:
 * calibre check → field rules → preview → import, brand-mismatch stop included.
 *
 * Download stays, next to this, as the fallback — for a result someone wants to
 * read, diff, or hand to something that is not this admin.
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { BulkImport } from '../CuratedWatches/BulkImport';
import {
  extractorErrorMessage,
  fetchBrandOverrides,
  getExtractorJobResult,
} from '../../services/extractorService';
import { reconcileEnvelopeBrandId } from '../../services/extractorBrandOverrides';
import { getWatchBrandById } from '../../services/firebase';
import type { WatchBrand } from '../../types';

interface ImportJobResultButtonProps {
  jobId: string;
  /** The brand the run was started for — stamped on the job by the admin. */
  brandId?: string;
  size?: 'sm' | 'md';
  variant?: 'primary' | 'secondary';
  label?: string;
  /** Fired after an import finishes, for a caller that lists what it wrote. */
  onImported?: () => void;
}

export function ImportJobResultButton({
  jobId,
  brandId,
  size = 'sm',
  variant = 'primary',
  label = 'Import into bulk import →',
  onImported,
}: ImportJobResultButtonProps) {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState<{ brand: WatchBrand; result: unknown } | null>(null);

  const handleClick = async () => {
    if (!brandId) {
      toast.error('This job records no brand — download the result and import it from the brand.');
      return;
    }

    setLoading(true);
    try {
      const [{ result }, brand, overrides] = await Promise.all([
        getExtractorJobResult(jobId),
        getWatchBrandById(brandId),
        fetchBrandOverrides(),
      ]);
      if (!brand) {
        toast.error(`No brand document “${brandId}” — it may have been renamed or deleted.`);
        return;
      }
      setPayload({ brand, result: reconcileEnvelopeBrandId(result, brand.id, overrides) });
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        loading={loading}
        disabled={loading}
        onClick={() => void handleClick()}
      >
        {label}
      </Button>

      {payload && (
        /*
         * Keyed on the job so a second import starts a wizard with no memory of
         * the first — the step, the item list and the rule choices are all
         * component state.
         */
        <BulkImport
          key={jobId}
          brand={payload.brand}
          autoOpen
          hideTriggerButton
          prefetchedExtraction={payload.result}
          onClose={() => setPayload(null)}
          onComplete={() => {
            setPayload(null);
            onImported?.();
          }}
        />
      )}
    </>
  );
}
